// lib/captureSubject.ts — WHICH RECORD THE KNOT PRE-ASSOCIATES A CAPTURE
// WITH, by detail screen, in THE BOOTED PACK'S OWN TYPES.
//
// S7 (Part A) introduced the screen → subject map in App.tsx; this is that
// map made pack-aware (GAP 57 readiness, 2026-09-29). It named
// `actor_audition`/`actor_project` for the two shared actor heads — but on
// actor_assist those pages show `aa_audition`/`aa_project` records, and the
// server keeps a subject only if the sent `subjectType` equals the record's
// real type (server/plugins/actorCapture.ts), so EVERY capture made on a
// CallToWrap audition or project page was dropped to a note.
//
// The pack is not imported here (a browser bundle has no business importing
// a server-side pack — FeatureSurfaces.test.ts states the same rule); its
// declared `captureSubjects` keys arrive through GET /auth/entry
// (api.getCaptureSubjectTypes). What this file still owns is the one thing
// only the web knows: which record types a SCREEN can be showing. A head two
// packs share lists each pack's type; the declaration picks.
//
// The rule, in order:
//   1. the first candidate the booted pack DECLARES as a capture subject;
//   2. otherwise, a screen with exactly ONE candidate names it — Rexx's
//      client and site pages are knot-intent context types without being
//      capture subjects, and their type is unambiguous;
//   3. otherwise (a shared head, declaration unknown or naming none of its
//      types) no type is sent. The server then resolves the subject from the
//      record's own type — never a guess that the type check will refuse.
import type { Route } from '../routes/route';

export const CAPTURE_SUBJECT_CANDIDATES: Partial<Record<Route['screen'], readonly string[]>> = {
  projectDetail: ['aa_project', 'actor_project'],
  auditionDetail: ['aa_audition', 'actor_audition'],
  workOrderDetail: ['hvac_work_order'],
  clientDetail: ['hvac_client'],
  siteDetail: ['hvac_site'],
  // S5a: the job screen's route id IS the work order's (routes/route.ts's
  // own note says why), so a capture taken standing in front of the job
  // pre-associates with the job.
  jobDetail: ['hvac_work_order'],
};

export function captureSubjectTypeFor(
  screen: Route['screen'],
  declaredSubjects: readonly string[] | null
): string | undefined {
  const candidates = CAPTURE_SUBJECT_CANDIDATES[screen];
  if (!candidates || candidates.length === 0) return undefined;
  if (declaredSubjects) {
    const declared = candidates.find((c) => declaredSubjects.includes(c));
    if (declared) return declared;
  }
  return candidates.length === 1 ? candidates[0] : undefined;
}

/** Whether a screen carries a subject at all (its route id is a record id). */
export function screenHasCaptureSubject(screen: Route['screen']): boolean {
  return CAPTURE_SUBJECT_CANDIDATES[screen] !== undefined;
}
